import { useMemo } from "react";
import { useHomeContextUser } from "../../homeContext.ts";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import type { DeweySchema } from "../../../root/helpers/brandSchema.ts";
import type { User } from "../../../../common/api/decimal/methods/user.ts";

interface SampleSearchEligibilityResult {
  isSampleSearchEnabled: boolean;
}

/**
 * Shared function to check if sampling search is eligible for a user.
 * Can be used in both hooks and loaders.
 */
export function isSampleSearchEligible(
  brand: DeweySchema,
  user: User
): boolean {
  // First check: If sampleSearchEnabled is false, don't show toggle
  if (!brand.InstructorHome.SamplingSearch.enabled) {
    return false;
  }
  if (!user.instructor.hasRole) {
    return false;
  }
  // If user is verified and has "us" in verified regions, show toggle
  if (
    user.instructor.verified &&
    user.instructor.verifiedRegions.includes("us")
  ) {
    return true;
  }
  // A "us"-blocked instructor is never eligible, even via the
  // institution-based fallback below
  if (user.instructor.blockedRegions?.includes("us")) {
    return false;
  }
  // Otherwise, fall back to checking whether any institution has
  // physicalCountry === "US"
  return (
    user.instructor.institutions?.some(
      (institution) => institution.physicalCountry === "US"
    ) ?? false
  );
}

export default function useSampleSearchEligibility(): SampleSearchEligibilityResult {
  const user = useHomeContextUser();
  const brand = useBrandLoaderData();

  const isSampleSearchEnabled = useMemo(() => {
    return isSampleSearchEligible(brand, user);
  }, [brand, user]);

  return {
    isSampleSearchEnabled,
  };
}
